import React from 'react';
// components
import Theme from './Theme.js';
import LinkAuthFlow from './LinkAuthFlow.js';
import Link from './Link.js';
// hooks
import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
// styles
import styles from './GateLivestreamPlus.module.css';
import Gate from './Gate.js';
// icons + logos
import IconPlusLogo from './IconPlusLogo.js';
import LogoPro from './LogoPro.js';
import IconArrowRight from './IconArrowRight.js';
import GateLivestreamPlusListItem from './GateLivestreamPlusListItem.js';

/**
 * GateLivestreamPlus: A component for displaying a CNBC+ Gate, a paywall for watching CNBC Livestreams that requires a subscription.
 * @component
 * @returns {JSX.Element} The rendered JSX element.
 */
const GateLivestreamPlus = () => {
  const { learnMoreUrl, cableProviderUrl, product } =
    useSearchParamOrDefaultProps();

  const textList = [
    'Global Livestream',
    'On-Demand Full Episodes',
    'Market Data Livestream',
    'Access Anytime, On the Go',
  ];

  const priceDollars = 14;
  const priceCents = 99;
  const priceInterval = 'month';
  const disclaimerText =
    'Currently on cnbc.com and iOS & Android apps. Full episodes cannot be accessed outside the U.S until late 2024.';
  const subscribeButtonText = 'Subscribe to CNBC+';

  return (
    <Theme>
      <Gate>
        <section className={styles.GateLivestreamPlusSection}>
          <div className={styles.backgroundDark}>
            <div className={styles.containerPaywallLiveTV}>
              <div className={styles.headerBox}>
                <div className={styles.headerContainer}>
                  <div className={styles.leadHeading}>
                    Watch the CNBC Livestream
                  </div>
                  <div className={styles.subLeadHeading}>
                    Watch every minute of market coverage and stay informed on
                    all breaking <br />
                    news, opinions, and analysis from pre-market buzz to the
                    closing bell, and <br /> beyond. Miss a moment? Catch up
                    with full episodes on-demand.
                  </div>
                </div>
              </div>
              <div className={styles.gateContainer}>
                <div className={styles.plusContent}>
                  <div className={styles.plusHeader}>Subscribe Now</div>
                  <div className={styles.topGateBox}>
                    <div className={styles.plusPaywall}>
                      <div className={styles.logoContainer}>
                        <IconPlusLogo />
                      </div>
                      <div className={styles.paywallBoard}>
                        <div className={styles.paywallHeader}>
                          Join CNBC+ now, and start Streaming Today
                        </div>
                        <ul className={styles.paywallText}>
                          {textList.map((text) => (
                            <GateLivestreamPlusListItem
                              key={text.split(' ').join()}
                              text={text}
                            />
                          ))}
                        </ul>
                      </div>
                      <div className={styles.price}>
                        <span
                          className={styles.priceValue}
                        >{`$${priceDollars}.`}</span>
                        <span className={styles.decimalValue}>
                          {priceCents}
                        </span>
                        &nbsp;/{priceInterval}
                      </div>
                      <div className={styles.promoText}>
                        <em>{`${disclaimerText} `} </em>
                      </div>

                      <LinkAuthFlow
                        className={styles.ctaButton}
                        product={product}
                        modalType={'signUp'}
                        contentType={'livestream'}
                        width={'100%'}
                        display={'inline-block'}
                        fontWeight={700}
                        letterSpacing={'normal'}
                        textAlign={'center'}
                        boxSizing={'border-box'}
                      >
                        {subscribeButtonText}
                      </LinkAuthFlow>
                      <div className={styles.learnSignInButton}>
                        <Link
                          className={styles.clickBut}
                          target="_top"
                          to={learnMoreUrl}
                          letterSpacing={'normal'}
                        >
                          Learn More
                        </Link>
                        <span className={styles.learnOr}>&nbsp;or&nbsp;</span>{' '}
                        <LinkAuthFlow
                          className={styles.clickBut}
                          product={product}
                          modalType={'signIn'}
                          letterSpacing={'normal'}
                          contentType={'livestream'}
                        >
                          Sign In
                        </LinkAuthFlow>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.divider}>
                  <span>or</span>
                </div>

                <div className={styles.signInContainer}>
                  <div className={styles.plusHeader}>Sign In</div>
                  <div className={styles.signInBox}>
                    <div className={styles.cableContainer}>
                      <div className={styles.subText}>
                        Prefer to watch CNBC via cable?
                      </div>
                      <div className={styles.logoPro}></div>
                      <Link
                        to={cableProviderUrl}
                        className={styles.signInButton}
                        target={'_top'}
                        letterSpacing={'normal'}
                      >
                        Choose your provider{' '}
                        <span>
                          <IconArrowRight />
                        </span>
                      </Link>
                    </div>

                    <div className={styles.proContainer}>
                      <div className={styles.subText}>
                        Already a subscriber?
                      </div>
                      <div className={styles.logoPro}>
                        <LogoPro />
                      </div>
                      <LinkAuthFlow
                        className={styles.signInButton}
                        product={'pro'}
                        modalType={'signIn'}
                        letterSpacing={'normal'}
                        contentType={'livestream'}
                      >
                        Sign in here{' '}
                        <span>
                          <IconArrowRight />
                        </span>
                      </LinkAuthFlow>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.termsAndConditions}>
                Have questions?&nbsp;
                <Link
                  to="https://cnbc.zendesk.com/hc/en-us"
                  target={'_top'}
                  letterSpacing={'normal'}
                >
                  Click here for FAQs
                </Link>
                &nbsp;or&nbsp;
                <br />
                <Link
                  to="mailto:subscriptions@cnbc.com"
                  target={'_top'}
                  letterSpacing={'normal'}
                >
                  Contact a Customer Care Rep
                </Link>
              </div>
            </div>
          </div>
        </section>
      </Gate>
    </Theme>
  );
};

GateLivestreamPlus.displayName = 'GateLivestreamPlus';

export default GateLivestreamPlus;
